import Vue from 'vue';
import siteTracker from '@/components/base-components/Trackers/site24x7';
import {CUSTOM_EVENTS} from '@/const';

const FLOW_ARRAY = [];
const clearComponentName = (tag) => tag?.replace?.(/vue-component-\d*-/, '') || 'Anonymous';
const getComponentName = (c) => c?.$options?.name || clearComponentName(c?.$vnode?.tag);
const getVueComponentsTree = (component) => {
  try {
    const tree = [];
    let parent = component?.$options?.parent;
    let maxDeep = 4;
    while (maxDeep--) {
      if (parent) {
        tree.push(getComponentName(parent));
        parent = parent?.$options?.parent;
      }
    }
    return tree.reverse().join(' -> ');
  } catch (e) {
    console.error(e);
  }
};
const buildNodeString = (el) => {
  if (!el) return 'element not built or not found';
  try {
    const node = {
      name: el.localName,
      attributes: Object.entries(el.attributes || {}).reduce((acc, [_, {name, value}]) => {
        if (!value && /data-v-/.test(name)) return acc;
        const parts = [name];
        value && parts.push(`"${value}"`);

        acc.push(`${parts.join('=')}`);
        return acc;
      }, []).join(' '),
    };
    const elNode = [node.name];
    node.attributes && elNode.push(node.attributes);

    return `<${elNode.join(' ')}>`;
  } catch (err) {
    console.error(err);
    return '';
  }
};
const getDOMComponentsTree = (element) => {
  if (!element) return 'element not built or not found';

  try {
    const tree = [];
    let el = element;
    let maxDeep = 4;

    while (maxDeep--) {
      if (el?.__vue__) {
        maxDeep = 0;
        tree.push(getVueComponentsTree(el.__vue__));
      } else if (el?.parentNode) {
        tree.push(buildNodeString(el));
        el = el.parentNode;
      } else {
        maxDeep = 0;
        break;
      }
    }
    return tree.reverse().join(' -> ');
  } catch (err) {
    console.error(err);
    return '';
  }
};
window.addEventListener('click', (e) => {
  try {
    const component = e?.target?.__vue__;

    const vue = {
      componentName: getComponentName(component),
      props: component?.$options.propsData,
      parentTree: getDOMComponentsTree(e?.target),
    };

    FLOW_ARRAY.push({
      onSiteTime: e.timeStamp,
      target: buildNodeString(e?.target),
      vue,
      type: 'click',
    });
  } catch (err) {
    console.error(err);
  }
});

window.document.addEventListener(CUSTOM_EVENTS.ROUTE_UPDATED, (e) => {
  try {
    FLOW_ARRAY.push({
      onSiteTime: e.timeStamp,
      from: e.detail?.referrer,
      to: e.detail?.to,
      type: 'navigate',
    });
  } catch (err) {
    console.error(err);
  }
});


// 1. Vue component errors
Vue.config.errorHandler = (err, vm, info) => {
  sendErrorReport({
    type: 'vue-error',
    errorName: err.name,
    message: err.message,
    component: getComponentName(vm),
    info: {
      el: buildNodeString(vm?.$el),
      innerText: vm?.$el?.innerText?.slice(0, 100) || '',
      tree: getVueComponentsTree(vm),
      message: info,
      stack: err.stack?.length > 300 ? err.stack?.slice(0, 300) : err.stack,
    }, // e.g. "render", "mounted hook"
  });
};

// 2. Vue warnings (dev only, but useful)
Vue.config.warnHandler = (msg, vm, trace) => {
  sendErrorReport({
    type: 'vue-warning',
    errorName: 'VueWarning',
    message: msg,
    component: getComponentName(vm),
    info: {
      el: buildNodeString(vm?.$el),
      innerText: vm?.$el?.innerText?.slice(0, 100) || '',
      tree: getVueComponentsTree(vm),
      stack: trace?.length > 300 ? trace?.slice(0, 300) : trace,
    },
  });
};

// 3. Unhandled JS errors (outside Vue)
window.addEventListener('error', (message= {error: {message: '', stack: ''}}) => {
  const {
    filename,
    error: {
      message: errorMessage,
      stack: errorStack,
    },
    lineno,
    colno,
  } = message;
if (/chrome-extension/.test(errorStack) || /setupExtension/.test(errorStack)) return;

  sendErrorReport({
    type: 'js-error',
    errorName: message?.error?.name,
    message: errorMessage,
    component: '',
    info: {
      filename,
      stack: errorStack,
      line: lineno,
      column: colno,
    },
  });
});

// 4. Unhandled promise rejections
const promiseRejectCallback = (event) => {
  const error = event?.reason || event?.detail || {};
  const {
    stack,
    ...reason
  } = error;

  if (/chrome-extension/.test(stack) || /setupExtension/.test(stack)) return;
  if (error?.name === 'AbortError' && /THEOplayer/.test(stack)) return;

  const info = {
    el: buildNodeString(event.target),
    tree: getDOMComponentsTree(event.target),
    reason,
    stack: stack?.length > 300 ? stack?.slice(0, 300) : stack,
  };

  sendErrorReport({
    type: 'promise-rejection',
    errorName: error?.name,
    message: error?.message || String(error),
    component: '',
    info,
  });
};

window.addEventListener('unhandledrejection', promiseRejectCallback);
window.document.addEventListener(CUSTOM_EVENTS.ROUTE_ERROR, promiseRejectCallback);

/**
 * Sends an error report to the Site24x7 tracker.
 * This function acts as a wrapper around the `sendCodeError` method of the `siteTracker` object,
 * forwarding the provided error details for logging and monitoring.
 *
 * @param {Object} error - The error object containing details about the error.
 * @param {string} error.type - The type of the error (e.g., 'vue-error', 'js-error').
 * @param {string} error.errorName - The name of the error.
 * @param {string} error.message - The error message.
 * @param {string} error.stack - The stack trace of the error.
 * @param {string} error.component - The name of the component where the error occurred.
 * @param {Object} error.info - Additional information about the error.
 *
 * @return {void}
 */
function sendErrorReport(error) {
  siteTracker.sendCodeError({
    userFlow: [...FLOW_ARRAY.slice(-1 * Math.min(FLOW_ARRAY.length, 10))],
    onSiteTime: parseInt(String(performance?.now?.())),
    ...error,
  });
  FLOW_ARRAY.length = 0;
}
